/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { useState, useEffect } from 'react';
import { Button, Message, Spin, Empty, Modal } from '@arco-design/web-react';
import { useDispatch, useSelector } from 'react-redux';
import { isMobile } from '@/utils/utils';
import { useJoin, useScene } from '@/lib/useCommon';
import { 
  updateFullScreen, 
  updateShowSubtitle,
  localLeaveRoom,
  switchRoom,
  clearHistoryMsg,
  clearCurrentMsg,
  leaveChatPage,
  updateAIGCState,
} from '@/store/slices/room';
import { RootState } from '@/store';
import { updateApiKey } from '@/store/slices/auth';
import RtcClient from '@/lib/RtcClient';
import request from '@/utils/request';
import API_CONFIG from '@/config/api';
import styles from './ConversationList.module.less';

interface ChatTopic {
  id: number;
  session_id: string;
  username: string;
  title: string;
  description: string;
  created_at: string;
  updated_at: string;
}

interface ChatTopicsResponse {
  code: number;
  message: string;
  data: ChatTopic[];
}

interface SessionIdResponse {
  code: number;
  message: string;
  data: {
    username: string;
    session_id: string;
  };
}

interface Conversation {
  id: string;
  session_id: string;
  title: string;
  description: string;
  timestamp: string;
  isActive?: boolean;
}

function ConversationList() {
  const dispatch = useDispatch();
  const [joining, dispatchJoin] = useJoin();
  const { isScreenMode, isAvatarScene } = useScene();
  const { isJoined, isAIGCEnable, scene } = useSelector((state: RootState) => state.room);
  const { username } = useSelector((state: RootState) => state.auth);
  
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState<string>('');

  // 格式化时间戳
  const formatTimestamp = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diff = now.getTime() - date.getTime();
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      
      if (days === 0) {
        const hours = Math.floor(diff / (1000 * 60 * 60));
        if (hours === 0) {
          const minutes = Math.floor(diff / (1000 * 60));
          return minutes === 0 ? '刚刚' : `${minutes}分钟前`;
        }
        return `${hours}小时前`;
      }
      if (days === 1) {
        return '昨天';
      }
      if (days < 7) {
        return `${days}天前`;
      }
      
      // 超过7天显示日期
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${month}/${day}`;
    } catch (error) {
      return dateString;
    }
  };

  // 加载会话列表
  const loadConversations = async () => {
    setLoading(true);
    try {
      const response = await request.get<ChatTopicsResponse>(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CHAT_TOPICS_LIST}`
      );

      if (response.code === 200 && response.data) {
        const formattedConversations: Conversation[] = response.data.map((topic) => ({
          id: String(topic.id),
          session_id: topic.session_id,
          title: topic.title,
          description: topic.description,
          timestamp: formatTimestamp(topic.updated_at),
          isActive: false,
        }));
        console.log('================会话列表========================',response.data);
        setConversations(formattedConversations);
        
        // 不默认选中任何会话，用户需要手动点击选择
        console.log('%c📋 [ConversationList] 会话列表加载完成，共', 'color: #13c2c2; font-weight: bold', formattedConversations.length, '条记录');
      } else {
        Message.error(response.message || '获取会话列表失败');
      }
    } catch (error: any) {
      console.error('获取会话列表失败:', error);
      Message.error('获取会话列表失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 组件挂载时加载会话列表
  useEffect(() => {
    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #fa8c16');
    console.log('%c📋 [ConversationList] 组件挂载，开始加载会话列表', 'color: #fa8c16; font-weight: bold; font-size: 13px');
    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #fa8c16');
    console.log('时间:', new Date().toLocaleTimeString());
    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #fa8c16');
    loadConversations();
  }, []);

  // 监听 SSE 话题列表更新事件
  useEffect(() => {
    const handleChatTopicUpdate = (event: CustomEvent) => {
      const data = event.detail;
      
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #1890ff');
      console.log('%c📋 [ConversationList] 收到 SSE 话题列表数据', 'color: #1890ff; font-weight: bold; font-size: 13px');
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #1890ff');
      console.log('数据:', data);
      
      if (data.data) {
        const topicData = data.data;
        
        // 创建新的会话对象
        const newConversation: Conversation = {
          id: String(topicData.id),
          session_id: topicData.session_id || data.session_id,
          title: topicData.title || '新会话',
          description: topicData.description || '',
          timestamp: formatTimestamp(topicData.updated_at || topicData.created_at || new Date().toISOString()),
          isActive: false,
        };
        
        // 检查是否已存在该会话（根据 id 判断）
        setConversations((prev) => {
          const existingIndex = prev.findIndex(conv => conv.id === newConversation.id);
          
          if (existingIndex >= 0) {
            // 已存在，更新该会话
            console.log('%c🔄 [ConversationList] 更新已有会话:', 'color: #faad14; font-weight: bold', newConversation.id);
            const updated = [...prev];
            updated[existingIndex] = newConversation;
            return updated;
          }
          
          // 不存在，添加到列表头部
          console.log('%c➕ [ConversationList] 添加新会话到列表:', 'color: #52c41a; font-weight: bold', newConversation.id);
          return [newConversation, ...prev];
        });
        
        console.log('%c✅ [ConversationList] 会话列表已更新', 'color: #52c41a; font-weight: bold');
        console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #1890ff');
      }
    };

    window.addEventListener('chatTopicUpdate', handleChatTopicUpdate as EventListener);

    return () => {
      window.removeEventListener('chatTopicUpdate', handleChatTopicUpdate as EventListener);
    };
  }, []);

  const handleNewConversation = async () => {
    // 如果正在加入中，不重复执行
    if (joining) {
      return;
    }

    try {
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #1890ff');
      console.log('%c🆕 [ConversationList] 开启新会话，调用 GET_SESSION_ID 接口', 'color: #1890ff; font-weight: bold; font-size: 13px');
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #1890ff');

      // 1. 调用 GET_SESSION_ID 接口获取新的 api_key
      const response = await request.post<SessionIdResponse>(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_SESSION_ID}`,
        {
          body: {
            username,
          },
          timeout: API_CONFIG.TIMEOUT,
        }
      );

      if (response.code === 200 && response.data?.session_id) {
        // eslint-disable-next-line camelcase
        const { session_id } = response.data;

        console.log('%c✅ [ConversationList] 获取 session_id 成功', 'color: #52c41a; font-weight: bold');
        // eslint-disable-next-line camelcase
        console.log('Session ID:', session_id);

        // 触发新会话开始事件，通知其他组件（如 EmotionAnalysis）清除旧数据
        console.log('%c🔄 [ConversationList] 触发 newSessionStarted 事件', 'color: #faad14; font-weight: bold');
        window.dispatchEvent(new CustomEvent('newSessionStarted', {
          // eslint-disable-next-line camelcase
          detail: { sessionId: session_id }
        }));

        // 2. 更新 auth store 中的 apiKey（使用 session_id 作为 apiKey，用于 SSE 连接）
        // eslint-disable-next-line camelcase
        dispatch(updateApiKey(session_id));

        // 3. 设置 Authorization header（使用 session_id）
        request.setDefaultHeaders({
          // eslint-disable-next-line camelcase
          'Authorization': session_id,
          // eslint-disable-next-line camelcase
          'x-api-key': session_id,
        });

        console.log('%c🔑 [ConversationList] 已更新 Authorization header 和 auth store', 'color: #52c41a; font-weight: bold');
        console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #52c41a');
      } else {
        console.error('获取 session 失败:', response.message);
        Message.error(response.message || '获取会话凭证失败');
        return;
      }
    } catch (error: any) {
      console.error('❌ 获取 session_id 失败:', error);
      Message.error('获取会话凭证失败，请重试');
      return;
    }

    // 3. 创建新对话：加入房间并开启对话
    dispatch(updateFullScreen({ isFullScreen: !isMobile() && !isScreenMode && !isAvatarScene }));
    dispatch(updateShowSubtitle({ isShowSubtitle: !isAvatarScene }));

    // 清除选中状态
    setActiveConversationId('');

    dispatchJoin();
  };

  const handleSelectConversation = async (id: string, sessionId: string) => {
    // 如果正在加入中，不重复执行
    if (joining) {
      return;
    }

    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #722ed1');
    console.log('%c📂 [ConversationList] 选中已有会话，开启对话', 'color: #722ed1; font-weight: bold; font-size: 13px');
    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #722ed1');
    console.log('会话 ID:', id);
    console.log('Session ID:', sessionId);

    // 更新选中状态
    setActiveConversationId(id);

    // 如果当前已在房间中，需要先退出（使用 switchRoom 保持页面状态，避免闪烁）
    if (isJoined) {
      console.log('%c🚪 [ConversationList] 当前已在房间中，切换会话...', 'color: #faad14; font-weight: bold');
      try {
        if (isAIGCEnable && scene) {
          await RtcClient.stopAgent(scene);
          dispatch(updateAIGCState({ isAIGCEnable: false }));
        }
        RtcClient.leaveRoom();
        // 使用 switchRoom 替代 localLeaveRoom，保持 isInChatPage 状态不变
        dispatch(switchRoom());
        dispatch(clearHistoryMsg());
        dispatch(clearCurrentMsg());
        console.log('%c✅ [ConversationList] 已切换会话，准备加入新房间', 'color: #52c41a; font-weight: bold');
      } catch (error) {
        console.warn('⚠️ 切换会话失败:', error);
      }
    }

    // 触发新会话开始事件，通知其他组件（如 EmotionAnalysis）清除旧数据
    console.log('%c🔄 [ConversationList] 触发 newSessionStarted 事件', 'color: #faad14; font-weight: bold');
    window.dispatchEvent(new CustomEvent('newSessionStarted', {
      detail: { sessionId }
    }));

    // 更新 auth store 中的 apiKey（使用 session_id 作为 apiKey，用于 SSE 连接）
    dispatch(updateApiKey(sessionId));

    // 设置 Authorization header（使用 session_id）
    request.setDefaultHeaders({
      'Authorization': sessionId,
      'x-api-key': sessionId,
    });

    console.log('%c🔑 [ConversationList] 已更新 Authorization header 和 auth store', 'color: #52c41a; font-weight: bold');

    // 触发全局事件，通知其他组件会话已变更
    window.dispatchEvent(new CustomEvent('conversationChanged', {
      detail: { sessionId }
    }));

    // 更新 UI 状态
    dispatch(updateFullScreen({ isFullScreen: !isMobile() && !isScreenMode && !isAvatarScene }));
    dispatch(updateShowSubtitle({ isShowSubtitle: !isAvatarScene }));

    // 加入房间并开启对话
    console.log('%c🚀 [ConversationList] 开始加入房间...', 'color: #1890ff; font-weight: bold');
    dispatchJoin();
    
    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #722ed1');
  };

  // 处理关闭按钮点击
  const handleClose = () => {
    Modal.confirm({
      title: '确认退出对话',
      content: '退出对话将结束当前会话并返回主页，确定要退出吗？',
      okText: '确定退出',
      cancelText: '取消',
      wrapClassName: styles.confirmModal,
      onOk: async () => {
        console.log('========================================');
        console.log('🚪 开始退出对话流程...');

        try {
          // 1. 如果正在 AI 对话中，停止 Agent
          if (isAIGCEnable && scene) {
            console.log('🛑 正在停止 AI Agent...');
            try {
              await RtcClient.stopAgent(scene);
              dispatch(updateAIGCState({ isAIGCEnable: false }));
              console.log('✅ AI Agent 已停止');
            } catch (error) {
              console.warn('⚠️ 停止 AI Agent 失败:', error);
            }
          }

          // 2. 如果已加入房间，离开房间
          if (isJoined) {
            console.log('🚪 正在离开 RTC 房间...');
            try {
              RtcClient.leaveRoom();
              dispatch(localLeaveRoom());
              console.log('✅ 已离开 RTC 房间');
            } catch (error) {
              console.warn('⚠️ 离开房间失败:', error);
            }
          }

          // 3. 清除对话相关数据
          console.log('🧹 正在清除对话数据...');
          dispatch(clearHistoryMsg());
          dispatch(clearCurrentMsg());
          dispatch(leaveChatPage());

          console.log('✅ 退出对话成功！返回主页');
          console.log('========================================');

          Message.success('已退出对话');
        } catch (error) {
          console.error('❌ 退出对话过程中发生错误:', error);
          // 即使出错也要清除状态
          dispatch(clearHistoryMsg());
          dispatch(clearCurrentMsg());
          dispatch(leaveChatPage());
          if (isJoined) {
            dispatch(localLeaveRoom());
          }
        }
      },
    });
  };

  return (
    <div className={styles.conversationList}>
      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.title}>会话列表</h2>
        <button className={styles.closeBtn} onClick={handleClose}>×</button>
      </div>

      {/* New Conversation Button */}
      <Button
        type="primary"
        long
        className={styles.newConversationBtn}
        onClick={handleNewConversation}
      >
        <span className={styles.plusIcon}>+</span>
        开启新会话
      </Button>

      {/* Conversation Items */}
      <div className={styles.conversationItems}>
        {loading ? (
          <div className={styles.loadingContainer}>
            <Spin />
            <div className={styles.loadingText}>加载中...</div>
          </div>
        ) : conversations.length === 0 ? (
          <Empty
            description="暂无会话记录"
            style={{ marginTop: '40px' }}
          />
        ) : (
          conversations.map(conv => (
            <div
              key={conv.id}
              className={`${styles.conversationItem} ${activeConversationId === conv.id ? styles.active : ''}`}
              onClick={() => handleSelectConversation(conv.id, conv.session_id)}
            >
              <div className={styles.conversationIcon}>💬</div>
              <div className={styles.conversationContent}>
                <div className={styles.conversationTitle}>{conv.title}</div>
                <div className={styles.conversationTimestamp}>{conv.timestamp}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default ConversationList;
